import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

import styles from './styles/AccomplishmentsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Accomplishment {
  accomplishment_id: number;
  name: string;
}

interface AccomplishmentDetails {
  accomplishment_id: number;
  name: string;
  grants_count?: number;
}

const AccomplishmentsPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const [accomplishments, setAccomplishments] = useState<Accomplishment[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [accomplishmentToDelete, setAccomplishmentToDelete] = useState<AccomplishmentDetails | null>(null);
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);

  // Track page visit only once when component mounts
  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackPageVisit("Accomplishments", "/accomplishments");
      hasTrackedRef.current = true;
    }
  }, []);

  useEffect(() => {
    const fetchAccomplishments = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/accomplishments`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch accomplishments.");
          return;
        }

        const data = await response.json();
        setAccomplishments(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchAccomplishments();
  }, [token, selectedOrgID]);

  const checkAccomplishmentUsage = async (accomplishmentID: number): Promise<AccomplishmentDetails | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accomplishments/${accomplishmentID}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        console.error("Failed to fetch accomplishment details.");
        return null;
      }

      return await response.json();
    } catch (err) {
      console.error("Error fetching accomplishment details:", err);
      return null;
    }
  };

  const handleDeleteClick = async (accomplishmentID: number) => {
    const accomplishmentDetails = await checkAccomplishmentUsage(accomplishmentID);
    if (accomplishmentDetails) {
      setAccomplishmentToDelete(accomplishmentDetails);
    }
  };

  const handleDelete = async (accomplishmentID: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accomplishments/${accomplishmentID}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        console.error("Failed to delete accomplishment.");
        return;
      }

      setAccomplishments(accomplishments.filter((a) => a.accomplishment_id !== accomplishmentID));
      setAccomplishmentToDelete(null);
    } catch (err) {
      console.error("Error deleting accomplishment:", err);
    }
  };

  const navigateToCreate = () => {
    navigate("/accomplishments/create");
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  const filteredAccomplishments = accomplishments.filter((accomplishment) =>
    accomplishment.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Accomplishments</h1>

      <div className={styles.searchContainer}>
        <input
          type="text"
          id="search"
          className={styles.searchInput}
          placeholder="Search by accomplishment name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <button onClick={navigateToCreate} className={styles.createButton}>
        New Accomplishment
      </button>

      {accomplishmentToDelete && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setAccomplishmentToDelete(null)} />
          <div className={styles.dialog}>
            <div className={styles.dialogContent}>
              {accomplishmentToDelete.grants_count ? (
                <>
                  Accomplishment "<em>{accomplishmentToDelete.name}</em>" has been granted to {accomplishmentToDelete.grants_count} user{accomplishmentToDelete.grants_count !== 1 ? 's' : ''}.<br /><br />
                  Deleting this accomplishment will remove it from {accomplishmentToDelete.grants_count !== 1 ? 'those users' : 'that user'}.
                </>
              ) : (
                <>
                  Accomplishment "<em>{accomplishmentToDelete.name}</em>" hasn't been granted to any users.
                </>
              )}
              <br /><br />
              This action CANNOT BE UNDONE. Are you sure you want to delete it?
            </div>
            <div className={styles.dialogButtons}>
              <button onClick={() => handleDelete(accomplishmentToDelete.accomplishment_id)} className={styles.dialogButton}>
                Yes, Delete
              </button>
              <button onClick={() => setAccomplishmentToDelete(null)} className={styles.dialogCancelButton}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>Accomplishment</th>
            <th className={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAccomplishments.length > 0 ? (
            filteredAccomplishments.map((a) => (
              <tr key={a.accomplishment_id} className={styles.tableRow}>
                <td className={styles.tableCell}>
                  <a
                    href="#"
                    className={styles.tableLink}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      navigate(`/accomplishments/${a.accomplishment_id}`);
                    }}
                  >
                    {a.name}
                  </a>
                </td>
                <td className={styles.tableCell}>
                  <button onClick={() => handleDeleteClick(a.accomplishment_id)} className={styles.deleteButton}>
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={2} className={styles.tableCell}>No accomplishments found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AccomplishmentsPage;
